import { useState } from "react";
import { useForm } from "react-hook-form";

import { getPublicConfig } from "../../../api/config/getPublicConfig";
import { getQuote } from "../../../api/quote/getQuote";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../../components/form/APAutoComplete";
import APForm from "../../../components/form/APForm";
import CopyUrlField from "../../../components/utils/CopyUrlField";
import { getAPExternalClientURL } from "../../../helpers/configuration";
import {
  buildQuoteQueryString,
  QuoteParameterOptions,
  QuoteParameters,
} from "../../../helpers/quoteParams";

interface CreateQuoteUrlSectionProps {
  quoteId: string;
}

type CreateQuoteUrlilterInputs = {
  fields: AutoCompleteOption[] | null;
};

const CreateQuoteUrlSection = ({ quoteId }: CreateQuoteUrlSectionProps) => {
  const [fields, setFields] = useState<QuoteParameters>({});

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CreateQuoteUrlilterInputs>({
    mode: "onTouched",
  });

  const quoteResponse = getQuote(quoteId);
  const configMap = getPublicConfig();

  if (quoteResponse.isError) {
    throw quoteResponse.error;
  }

  // Build the query string
  const buildQuoteUrl = (hostUrl: string, quoteParams: QuoteParameters) => {
    const queryString = buildQuoteQueryString(quoteParams);
    return `${hostUrl}/create-quote${queryString}`;
  };

  const handleSubmitFilters = (inputs: CreateQuoteUrlilterInputs) => {
    if (quoteResponse.data) {
      const quote = quoteResponse.data;
      const quoteParams: QuoteParameters = {
        makeId: inputs.fields?.some((field) => field.id === "makeId")
          ? quote.vehicle.makeId
          : null,
        modelId: inputs.fields?.some((field) => field.id === "modelId")
          ? quote.vehicle.modelId
          : null,
        modelYear: inputs.fields?.some((field) => field.id === "modelYear")
          ? quote.vehicle.year
          : null,
        variantId: inputs.fields?.some((field) => field.id === "variantId")
          ? quote.vehicle.variantId
          : null,
        purchaseType: inputs.fields?.some(
          (field) => field.id === "purchaseType"
        )
          ? quote.vehicle.purchaseType
          : null,
        term: inputs.fields?.some((field) => field.id === "term")
          ? quote.term.termMonths.toString()
          : null,
        salary: inputs.fields?.some((field) => field.id === "salary")
          ? quote.salaryComparison.salaryExSuper.toString()
          : null,
        businessUsage: inputs.fields?.some(
          (field) => field.id === "businessUsage"
        )
          ? quote.vehicle.businessUsePercentage.toString()
          : null,
        discount: inputs.fields?.some((field) => field.id === "discount")
          ? quote.vehicle.discount?.toString() || null
          : null,
        listPrice: inputs.fields?.some((field) => field.id === "listPrice")
          ? quote.vehicle.listPrice.incGst.toString()
          : null,
      };

      setFields(quoteParams);
    }
  };

  const hostUrl = configMap.isSuccess
    ? getAPExternalClientURL(configMap.data)
    : null;
  const quoteUrl =
    hostUrl && Object.keys(fields).length > 0
      ? buildQuoteUrl(hostUrl, fields)
      : null;

  return (
    <>
      <APForm
        onSubmit={handleSubmit(handleSubmitFilters)}
        submitText={"Generate URL"}
        isLoading={false}
        isError={Object.keys(errors).length > 0}
        minSpacing
        submitButtonColor="primary"
      >
        <APAutoComplete
          name="fields"
          label="Fields"
          options={QuoteParameterOptions}
          control={control}
          validations={{ required: true }}
          errors={errors}
          defaultValue={QuoteParameterOptions.filter((option) =>
            [
              "makeId",
              "modelId",
              "modelYear",
              "variantId",
              "listPrice",
              "term",
            ].includes(option.id)
          )}
          setValue={setValue}
          multiple
        />
      </APForm>
      {quoteUrl && (
        <CopyUrlField
          url={quoteUrl}
          copyText="Copy the URL below to create a new quote with the same parameters as this quote:"
        />
      )}
    </>
  );
};

export default CreateQuoteUrlSection;
